import {
  AdditiveBlending,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  type Material,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  Points,
  PointsMaterial,
  type Scene,
  TorusGeometry,
  Vector3,
} from 'three';
import { EYE_HEIGHT } from '../movement/constants';
import type { PlayerController } from '../movement/PlayerController';

/** Spawn a speed portal on the player's path so they fly through it (Velocity Gate). */
export interface SpeedPortalOptions {
  /** Unit direction the gate sits along — the travel/aim line (NOT a fixed offset). */
  direction: Vector3;
  /** Horizontal-speed multiplier applied on pass-through. */
  boost: number;
  /** Lifetime in seconds (if never flown through). */
  durationSec: number;
  /** Pass-through trigger radius. */
  radius: number;
  /** Distance ahead of the player to place the gate. */
  distance: number;
  /** Tint (hero accent hex). */
  color: number;
}

interface PortalRecord {
  abilityId: string;
  center: Vector3;
  radius: number;
  boost: number;
  remaining: number;
  age: number;
  group: Group;
  ring: Mesh;
  ringMat: MeshStandardMaterial;
  sparkle: Points;
  entered: boolean;
}

interface BurstRecord {
  remaining: number;
  total: number;
  points: Points;
  mat: PointsMaterial;
  baseSize: number;
}

/**
 * Owns the transient world objects hero abilities spawn — speed gates (fly-through
 * boost), comet trails and sparkle bursts — and their full lifecycle: build,
 * animate (spin/twinkle), per-tick trigger/expiry, and disposal. Visuals are
 * emissive + additive so bloom makes them glow. One hero is active at a time, so a
 * single trail slot suffices.
 */
export class AbilityWorld {
  private player: PlayerController | null = null;
  private portals: PortalRecord[] = [];
  private bursts: BurstRecord[] = [];
  /** Active comet-trail emitter (ultimates), or null. */
  private trail: { remaining: number; color: number; emit: number } | null = null;

  constructor(private readonly scene: Scene) {}

  setPlayer(player: PlayerController): void {
    this.player = player;
  }

  /** True while the named ability's world object exists — drives the HUD glow. */
  isActive(abilityId: string): boolean {
    return this.portals.some((p) => p.abilityId === abilityId);
  }

  spawnSpeedPortal(abilityId: string, opts: SpeedPortalOptions): void {
    if (!this.player) {
      return;
    }
    // Place it on the player's line of travel at eye height so they fly through it.
    const eye = this.player.position.clone();
    eye.y += EYE_HEIGHT;
    const center = eye.addScaledVector(opts.direction, opts.distance);

    const ringMat = new MeshStandardMaterial({
      color: opts.color,
      emissive: opts.color,
      emissiveIntensity: 1.1,
      transparent: true,
      opacity: 0.4, // translucent; pulses in tick()
      depthWrite: false,
    });
    const ring = new Mesh(new TorusGeometry(opts.radius, 10, 18, 64), ringMat);
    const sparkle = this.makeRingSparkle(opts.radius, opts.color);

    const group = new Group();
    group.add(ring, sparkle);
    group.position.copy(center);
    group.lookAt(center.clone().add(opts.direction)); // ring opening faces the approach
    this.scene.add(group);

    this.portals.push({
      abilityId,
      center,
      radius: opts.radius,
      boost: opts.boost,
      remaining: opts.durationSec,
      age: 0,
      group,
      ring,
      ringMat,
      sparkle,
      entered: false,
    });
  }

  /** A quick expanding ring of sparks (ability bursts + the comet trail). */
  spawnSparkleBurst(
    center: Vector3,
    color: number,
    opts?: { count?: number; life?: number; size?: number; radius?: number },
  ): void {
    const count = opts?.count ?? 140;
    const life = opts?.life ?? 0.55;
    const size = opts?.size ?? 16;
    const radius = opts?.radius ?? 60;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      // Local offsets around the origin; the Points node sits at `center`.
      const dir = new Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5,
      ).normalize();
      const r = radius * (0.3 + Math.random() * 0.7);
      positions[i * 3] = dir.x * r;
      positions[i * 3 + 1] = dir.y * r;
      positions[i * 3 + 2] = dir.z * r;
    }
    const geom = new BufferGeometry();
    geom.setAttribute('position', new Float32BufferAttribute(positions, 3));
    const mat = new PointsMaterial({
      color,
      size,
      sizeAttenuation: true,
      transparent: true,
      opacity: 1,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const points = new Points(geom, mat);
    points.position.copy(center);
    points.scale.setScalar(0.2); // grows outward from the player in tick()
    this.scene.add(points);
    this.bursts.push({ remaining: life, total: life, points, mat, baseSize: size });
  }

  /** Ultimate flair: an ignition burst + a comet trail emitted at the player for `durationSec`. */
  startTrail(durationSec: number, color: number): void {
    if (!this.player) {
      return;
    }
    this.spawnSparkleBurst(this.player.position, color, {
      count: 90,
      life: 0.7,
      size: 18,
      radius: 70,
    });
    this.trail = { remaining: durationSec, color, emit: 0 };
  }

  /** Advance timers + animation, fire portal pass-throughs, and expire spent objects. */
  tick(dt: number): void {
    const player = this.player;
    if (!player) {
      return;
    }

    for (const portal of this.portals) {
      portal.remaining -= dt;
      portal.age += dt;
      portal.ring.rotation.z += dt * 1.6; // spin
      portal.sparkle.rotation.z -= dt * 2.4; // counter-spin sparkles
      const pulse = 0.5 + 0.5 * Math.sin(portal.age * 5);
      portal.ringMat.opacity = 0.3 + 0.22 * pulse; // shimmer translucency
      portal.ringMat.emissiveIntensity = 0.9 + 0.6 * pulse;
      const inside = player.position.distanceTo(portal.center) <= portal.radius;
      if (inside && !portal.entered) {
        portal.entered = true; // rising edge → fire once
        player.boostLateral(portal.boost);
        portal.remaining = 0; // consume on use
      }
    }
    for (const portal of this.portals.filter((p) => p.remaining <= 0)) {
      this.scene.remove(portal.group);
      disposeObject(portal.group);
    }
    this.portals = this.portals.filter((p) => p.remaining > 0);

    for (const burst of this.bursts) {
      burst.remaining -= dt;
      const t = Math.min(1, 1 - burst.remaining / burst.total); // 0 → 1
      burst.points.scale.setScalar(0.2 + t * 3.2); // expand
      burst.mat.opacity = Math.max(0, 1 - t); // fade
      burst.mat.size = burst.baseSize * (1 + t);
    }
    for (const burst of this.bursts.filter((b) => b.remaining <= 0)) {
      this.scene.remove(burst.points);
      disposeObject(burst.points);
    }
    this.bursts = this.bursts.filter((b) => b.remaining > 0);

    // Ultimate comet trail: stream small sparkle puffs at the player.
    if (this.trail) {
      this.trail.remaining -= dt;
      this.trail.emit += dt;
      while (this.trail.emit >= 0.05) {
        this.trail.emit -= 0.05;
        this.spawnSparkleBurst(player.position, this.trail.color, {
          count: 10,
          life: 0.45,
          size: 14,
          radius: 22,
        });
      }
      if (this.trail.remaining <= 0) {
        this.trail = null;
      }
    }
  }

  /** Remove and dispose every spawned object (respawn / map switch). */
  clear(): void {
    for (const portal of this.portals) {
      this.scene.remove(portal.group);
      disposeObject(portal.group);
    }
    this.portals = [];
    for (const burst of this.bursts) {
      this.scene.remove(burst.points);
      disposeObject(burst.points);
    }
    this.bursts = [];
    this.trail = null;
  }

  /** A ring of additive sparkles around a portal's circumference. */
  private makeRingSparkle(radius: number, color: number): Points {
    const count = 80;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const r = radius + (Math.random() - 0.5) * 26;
      positions[i * 3] = Math.cos(a) * r;
      positions[i * 3 + 1] = Math.sin(a) * r;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
    const geom = new BufferGeometry();
    geom.setAttribute('position', new Float32BufferAttribute(positions, 3));
    return new Points(
      geom,
      new PointsMaterial({
        color,
        size: 13,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.95,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    );
  }
}

function disposeObject(root: Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof Mesh || obj instanceof Points) {
      obj.geometry.dispose();
      const mat = obj.material as Material | Material[];
      if (Array.isArray(mat)) {
        for (const m of mat) m.dispose();
      } else {
        mat.dispose();
      }
    }
  });
}
